import { useCallback } from 'react';
import type { RecaptchaResult } from '@atlassian/google-recaptcha';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { CONFLUENCE } from '@atlassian/jira-shared-types';
import { useCloudId, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import useOpenInvitePeopleModal from '../common/ui/use-invite-people-modal';
import {
	IneligibilityReasons,
	useFeatureExposedV1,
	useFeatureExposedV2,
} from '../services/feature-exposed';
import { isInviteOnLinkInsertV3Enabled, SOURCE, type Source } from '../services/feature-flag';
import { useInvitees } from '../services/get-invitees';
import { getPageTitle } from '../services/get-page-title';
import { useInviteUsers } from '../services/invite';
import { useCanInvite } from '../services/invite-capability';
import { useOptOut } from '../services/opt-out';
import { type LinkData, isConfluenceLink } from '../utils';
import { invitePromptOnLinkInsertExperience } from './experiences';
import { useInviteErrorFlag } from './invite-error-flag';
import { useInvitePromptFlag } from './invite-prompt-flag';
import { useInviteSuccessFlag } from './invite-success-flag';

export const useInvitePromptExperimentV1 = (createIssueProjectKey?: string) => {
	const canInvite = useCanInvite();
	const getInvitees = useInvitees(createIssueProjectKey);
	const showInvitePromptFlag = useInvitePromptFlag();
	const showSuccessFlag = useInviteSuccessFlag();
	const showErrorFlag = useInviteErrorFlag();
	const inviteUsers = useInviteUsers();
	const { getOptOutStatus, setOptOutStatus, incrementViewCount, shouldShowOptOutOption } =
		useOptOut();
	const fireFeatureExposed = useFeatureExposedV1();
	const cloudId = useCloudId();
	const openInviteModal = useOpenInvitePeopleModal();
	return useCallback(
		async (continueUrl: string, data: LinkData, source: Source = SOURCE.ISSUE_VIEW) => {
			try {
				invitePromptOnLinkInsertExperience.start();
				const { tenantId, product, name } = data;
				const attributes = { source, product, isSameSite: tenantId === cloudId };

				const hasOptedOut = await getOptOutStatus();
				if (hasOptedOut) {
					fireFeatureExposed(IneligibilityReasons.OPTED_OUT, attributes);
					return;
				}

				const canInviteToProduct = await canInvite(tenantId, product);
				if (!canInviteToProduct) {
					fireFeatureExposed(IneligibilityReasons.MISSING_INVITE_CAPABILITY, attributes);
					return;
				}

				// V3 only works with V1 of AIOLI
				const enableThirtyInvitees =
					isInviteOnLinkInsertV3Enabled() && ff('aioli-v3-allow-30-users-from-core-invites', false);
				const inviteesLimit = enableThirtyInvitees ? 30 : 10;
				const users = await getInvitees(tenantId, product, inviteesLimit);
				if (users.length === 0) {
					fireFeatureExposed(IneligibilityReasons.NO_ELIGIBLE_INVITEES, attributes);
					return;
				}

				const targetPageLabel = name || (await getPageTitle(continueUrl));

				fireFeatureExposed(IneligibilityReasons.ELIGIBLE, attributes);
				if (!expValEquals('invite_prompt_on_link_insert', 'cohort', 'variation')) return;
				const shouldOpenCoreInvites = false;

				const onOpenCoreInvites = () => {
					if (shouldOpenCoreInvites) {
						openInviteModal(continueUrl, data, users);
					}
				};

				showInvitePromptFlag({
					targetPageLabel,
					product,
					users,
					allowOptOut: await shouldShowOptOutOption(),
					onSubmit: async (getRecaptchaToken: RecaptchaResult['getRecaptchaToken']) => {
						try {
							const recaptchaToken = await getRecaptchaToken('v1_invitations_invite').catch(
								() => undefined,
							);
							await inviteUsers(
								{
									continueUrl,
									users,
									tenantId,
									product,
								},
								recaptchaToken,
							);
							showSuccessFlag({ users, product });
						} catch {
							showErrorFlag();
						}
					},
					onDismiss: incrementViewCount,
					onOptOut: setOptOutStatus,
					shouldOpenCoreInvites,
					onOpenCoreInvites,
				});
				invitePromptOnLinkInsertExperience.success();
				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInvitePromptExperiment',
						packageName: 'jiraInvitePromptOnLinkInsert',
					},
					error,
				});
				invitePromptOnLinkInsertExperience.failure();
			}
		},
		[
			canInvite,
			cloudId,
			fireFeatureExposed,
			getInvitees,
			getOptOutStatus,
			incrementViewCount,
			inviteUsers,
			openInviteModal,
			setOptOutStatus,
			shouldShowOptOutOption,
			showErrorFlag,
			showInvitePromptFlag,
			showSuccessFlag,
		],
	);
};

export const useInvitePromptExperimentV2 = (createIssueProjectKey?: string) => {
	const canInvite = useCanInvite();
	const getInvitees = useInvitees(createIssueProjectKey);
	const showInvitePromptFlag = useInvitePromptFlag();
	const showSuccessFlag = useInviteSuccessFlag();
	const showErrorFlag = useInviteErrorFlag();
	const inviteUsers = useInviteUsers();
	const { getOptOutStatus, setOptOutStatus, incrementViewCount, shouldShowOptOutOption } =
		useOptOut();
	const fireFeatureExposed = useFeatureExposedV2();
	const cloudId = useCloudId();
	const openInviteModal = useOpenInvitePeopleModal();
	return useCallback(
		async (continueUrl: string, data: LinkData, source: Source = SOURCE.ISSUE_VIEW) => {
			try {
				invitePromptOnLinkInsertExperience.start();
				const { tenantId, product, name } = data;
				const attributes = { source, product, isSameSite: tenantId === cloudId };

				const hasOptedOut = await getOptOutStatus();
				if (hasOptedOut) {
					fireFeatureExposed(IneligibilityReasons.OPTED_OUT, attributes);
					return;
				}

				const canInviteToProduct = await canInvite(tenantId, product);
				if (!canInviteToProduct) {
					fireFeatureExposed(IneligibilityReasons.MISSING_INVITE_CAPABILITY, attributes);
					return;
				}

				const inviteesLimit = 10;
				const users = await getInvitees(tenantId, product, inviteesLimit);
				if (users.length === 0) {
					fireFeatureExposed(IneligibilityReasons.NO_ELIGIBLE_INVITEES, attributes);
					return;
				}

				const targetPageLabel = name || (await getPageTitle(continueUrl));

				fireFeatureExposed(IneligibilityReasons.ELIGIBLE, attributes);
				if (!expValEquals('invite_prompt_on_link_insert_v2', 'cohort', 'variation')) return;
				const shouldOpenCoreInvites = false;

				const onOpenCoreInvites = () => {
					if (shouldOpenCoreInvites) {
						openInviteModal(continueUrl, data, users);
					}
				};

				showInvitePromptFlag({
					targetPageLabel,
					product,
					users,
					allowOptOut: await shouldShowOptOutOption(),
					onSubmit: async (getRecaptchaToken: RecaptchaResult['getRecaptchaToken']) => {
						try {
							const recaptchaToken = await getRecaptchaToken('v1_invitations_invite').catch(
								() => undefined,
							);
							await inviteUsers(
								{
									continueUrl,
									users,
									tenantId,
									product,
								},
								recaptchaToken,
							);
							showSuccessFlag({ users, product });
						} catch {
							showErrorFlag();
						}
					},
					onDismiss: incrementViewCount,
					onOptOut: setOptOutStatus,
					shouldOpenCoreInvites,
					onOpenCoreInvites,
				});
				invitePromptOnLinkInsertExperience.success();
				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInvitePromptExperiment',
						packageName: 'jiraInvitePromptOnLinkInsert',
					},
					error,
				});
				invitePromptOnLinkInsertExperience.failure();
			}
		},
		[
			canInvite,
			cloudId,
			fireFeatureExposed,
			getInvitees,
			getOptOutStatus,
			incrementViewCount,
			inviteUsers,
			openInviteModal,
			setOptOutStatus,
			shouldShowOptOutOption,
			showErrorFlag,
			showInvitePromptFlag,
			showSuccessFlag,
		],
	);
};

export const useInviteOnCreateLink = (link: string) => {
	const cloudId = useCloudId();
	const cloudName = useTenantContext()?.cloudName;
	const triggerInviteExperiment = useInvitePromptExperimentV2();
	return useCallback(() => {
		if (isConfluenceLink(link, cloudName)) {
			triggerInviteExperiment(link, { tenantId: cloudId, product: CONFLUENCE }, SOURCE.CREATE_LINK);
		}
	}, [cloudId, cloudName, link, triggerInviteExperiment]);
};
